import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";

import { CreateTemplateRequest } from "./AssessmentSlice";
import type {
  OrganizationWeeklyDigestConfig,
  OrganizationProductTelemetricsConfig,
  OrganizationCoverageAnalysisConfig,
} from "../../Container/AdminOverviewContainer";
import { get, post, del, put } from "../../Services/ApiRequest";
import { API_URLS } from "../../Utils/Constants/APIConstants";
import type { PortfolioMapsConfig } from "../../Utils/Constants/portfolioMapLenses";
import { toast } from "../../Utils/Helper/toast";

interface Statistics {
  totalOrganizations: number;
  totalUsers: number;
  activeUsers: number;
}

interface OrganizationRlSettingConfig {
  relationship_assessments?: boolean;
}

interface OrganizationSettings {
  rl_setting_config?: OrganizationRlSettingConfig;
  weekly_digest?: OrganizationWeeklyDigestConfig;
  product_telemetrics?: OrganizationProductTelemetricsConfig;
  coverage_analysis?: OrganizationCoverageAnalysisConfig;
}

interface Organization {
  tenant_id: string;
  organization: string;
  domain: string;
  tenant_schema_name: string;
  users_count: number;
  status: string;
  created_at: string;
  settings?: OrganizationSettings;
}

interface AdminUser {
  user_id: string;
  user: string;
  email: string;
  organization: string;
  role: string;
  status: string;
  created_at: string;
  last_login_at: string;
}

interface OrganizationLevel {
  id: number;
  level_code: string;
  name: string;
  description: string;
  guidance?: string;
  created_at: string;
  updated_at: string;
}

interface AssessmentTemplate {
  template_id: string;
  name: string;
  type: string;
  description: string;
  is_global: boolean;
  json_schema: {
    metadata: {
      version: string;
      categories: string[];
      created_at: string;
    };
    questions: Array<{
      id: string;
      options: any;
      required: boolean;
      order_index: number;
      question_text: string;
      question_type: string;
    }>;
    categories: string[];
  };
  target_level: string;
  target_tier: string;
  target_industry: string;
  is_active: boolean;
  created_by: string;
  created_at: string;
  updated_at: string;
}

export interface AuthLog {
  log_id: string;
  user_id: string;
  email: string;
  event_type: string;
  ip_address: string;
  status: string;
  failure_reason: string | null;
  created_at: string;
}

export interface AuthLogsResponse {
  logs: AuthLog[];
  total: number;
  page: number;
  limit: number;
  total_pages: number;
}

export interface DeleteTenantNotificationState {
  type: "success" | "error";
  message: string;
  details: string;
}

interface AdminState {
  statistics: Statistics | null;
  organizations: Organization[];
  allUsers: AdminUser[];
  activeUsers: AdminUser[];
  organizationLevels: OrganizationLevel[];
  relationshipLevels: OrganizationLevel[];
  relationshipLevelsLoading: boolean;
  updateRelationshipLevelLoading: boolean;
  assessmentTemplates: AssessmentTemplate[];
  authLogsData: AuthLogsResponse | null;
  authLogsLoading: boolean;
  authLogsError: string | null;
  templatesLoading: boolean;
  templatesError: string | null;
  loading: boolean;
  error: string | null;
  createTenantLoading: boolean;
  createTenantError: string | null;
  createTenantSuccess: boolean;
  deleteTenantSuccess: boolean;
  deleteTenantLoading: boolean;
  deleteTenantError: string | null;
  deleteTenantNotification: DeleteTenantNotificationState | null;
  createLevelSuccess: boolean;
  createLevelLoading: boolean;
  createLevelError: string | null;
  deleteLevelSuccess: boolean;
  deleteLevelLoading: boolean;
  deleteLevelError: string | null;
  deleteUserSuccess: boolean;
  deleteUserLoading: boolean;
  deleteUserError: string | null;
  updateUserSuccess: boolean;
  updateUserLoading: boolean;
  updateUserError: string | null;
  resetPasswordSuccess: boolean;
  resetPasswordLoading: boolean;
  resetPasswordError: string | null;
  createUserSuccess: boolean;
  createUserLoading: boolean;
  createUserError: string | null;
  resendWelcomeEmailLoading: boolean;
  resendWelcomeEmailError: string | null;
  resendWelcomeEmailSuccess: boolean;
  isCreatingTemplate: boolean;
  createTemplateSuccess: boolean;
  createTemplateError: string | null;
  changeMyPasswordLoading: boolean;
  changeMyPasswordSuccess: boolean;
  healthCheckReport: any;
  healthCheckLoading: boolean;
  healthCheckError: string | null;
  healthCheckRepairLoading: boolean;
  ibHealthCheckReport: any;
  ibHealthCheckLoading: boolean;
  ibHealthCheckError: string | null;
  ibHealthCheckRepairLoading: boolean;
  ibHealthCheckRepairJob: any;
  bhBriefingSyncResult: any;
  bhBriefingSyncLoading: boolean;
  bhBriefingSyncError: string | null;
  bhBriefingAccounts: {
    orgId: string | null;
    organization: string | null;
    accounts: Array<{
      id: string;
      name: string;
      stored?: boolean;
      digest_date?: string | null;
    }>;
    loading: boolean;
    error: string | null;
  };
  dataIntegrityReport: any;
  dataIntegrityLoading: boolean;
  dataIntegrityError: string | null;
  updateOrgRlSettingLoading: boolean;
  updateOrgRlSettingTenantId: string | null;
  updateOrgWeeklyDigestTenantId: string | null;
  updateOrgProductTelemetricsTenantId: string | null;
  updateOrgCoverageAnalysisTenantId: string | null;
  orgProductTelemetry: OrgProductTelemetryState;
  adminMetadataInfo: {
    loading: boolean;
    error: string | null;
    data: any | null;
  };
  cronSettings: AdminCronSettingsState;
  portfolioMapsSettings: AdminPortfolioMapsSettingsState;
}

export interface AdminCronSettingsState {
  tempPublicBriefingParallelCount: number | null;
  loading: boolean;
  saving: boolean;
  loadError: string | null;
}

export interface AdminPortfolioMapsSettingsState {
  config: PortfolioMapsConfig | null;
  loading: boolean;
  saving: boolean;
  loadError: string | null;
}

export interface OrgMetricRow {
  id: string;
  org_id: string;
  role: string;
  screen_key?: string | null;
  metric_key: string;
  metric_date: string;
  metric_value: number;
}

export interface OrgMetricUserRow {
  id: string;
  org_id: string;
  role: string;
  screen_key?: string;
  metric_key: string;
  metric_date: string;
  person_id: string;
  email: string;
  name: string;
  activity_count: number;
}

export interface OrgProductTelemetryState {
  metrics: OrgMetricRow[];
  users: OrgMetricUserRow[];
  loading: boolean;
  error: string | null;
  orgId: string | null;
}

export interface ReleaseNoteVersion {
  id: string;
  version_label: string;
  title: string;
  body_html: string;
  created_at: string;
  updated_at: string;
}

export interface ReleaseNoteVersionList {
  items: ReleaseNoteVersion[];
  total: number;
}

export interface ReleaseNoteVersionWrite {
  version_label: string;
  title: string;
  body_html: string;
}

export interface ReleaseNotesRecipient {
  user_id: string;
  full_name: string;
  email: string;
  role?: string | null;
  status: string;
  last_login_at?: string | null;
  last_contacted_at?: string | null;
}

export interface ReleaseNotesOrgRecipientGroup {
  org_id: string;
  organization: string;
  users: ReleaseNotesRecipient[];
}

export interface ReleaseNotesRecipientsPayload {
  organizations: ReleaseNotesOrgRecipientGroup[];
}

export interface ReleaseNotesPreviewPayload {
  name: string;
  subject: string;
  header_banner: string;
  html: string;
}

export interface ReleaseNotesOrgSendResult {
  org_id: string;
  organization: string;
  sent_count: number;
  failed_count: number;
}

export interface ReleaseNotesSendResult {
  sent_count: number;
  failed_count: number;
  organizations: ReleaseNotesOrgSendResult[];
}

const unwrapOrgTelemetryList = <T>(payload: any): T[] => {
  if (Array.isArray(payload?.content?.items)) return payload.content.items;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.content)) return payload.content;
  return [];
};

/** ApiRequest wraps failures as Error(JSON.stringify({ status, message })). */
const getOrgTelemetryErrorMessage = (
  error: unknown,
  fallback: string
): string => {
  if (typeof error === "string" && error.trim()) return error;

  const anyError = error as {
    response?: { data?: { detail?: unknown; message?: unknown } };
    message?: string;
  };

  const direct =
    anyError?.response?.data?.detail || anyError?.response?.data?.message;
  if (typeof direct === "string" && direct.trim()) return direct;

  const raw = anyError?.message;
  if (typeof raw === "string" && raw.trim()) {
    try {
      const parsed = JSON.parse(raw);
      if (typeof parsed?.message === "string" && parsed.message.trim()) {
        return parsed.message;
      }
      if (typeof parsed?.detail === "string" && parsed.detail.trim()) {
        return parsed.detail;
      }
    } catch {
      // keep raw string fallback
    }
    return raw;
  }

  return fallback;
};

export interface HealthCheckScanPayload {
  org: string | null;
}

export interface HealthCheckRepairPayload {
  orgId?: string;
  repairAll?: boolean;
}

export interface IbHealthCheckScanPayload {
  org: string | null;
}

export interface IbHealthCheckRepairPayload {
  orgId?: string;
  repairAll?: boolean;
  generateMissingBriefings?: boolean;
  forceRegenerateAll?: boolean;
  accountIds?: string[];
}

export interface BusinessHealthBriefingSyncPayload {
  orgId?: string;
  syncAll?: boolean;
  briefingDate?: string;
  accountIds?: string[];
}

const initialState: AdminState = {
  statistics: null,
  organizations: [],
  allUsers: [],
  activeUsers: [],
  organizationLevels: [],
  relationshipLevels: [],
  relationshipLevelsLoading: false,
  updateRelationshipLevelLoading: false,
  assessmentTemplates: [],
  authLogsData: null,
  authLogsLoading: false,
  authLogsError: null,
  templatesLoading: false,
  templatesError: null,
  loading: false,
  error: null,
  createTenantLoading: false,
  createTenantError: null,
  createTenantSuccess: false,
  deleteTenantSuccess: false,
  deleteTenantLoading: false,
  deleteTenantError: null,
  deleteTenantNotification: null,
  createLevelSuccess: false,
  createLevelLoading: false,
  createLevelError: null,
  deleteLevelSuccess: false,
  deleteLevelLoading: false,
  deleteLevelError: null,
  deleteUserSuccess: false,
  deleteUserLoading: false,
  deleteUserError: null,
  updateUserSuccess: false,
  updateUserLoading: false,
  updateUserError: null,
  resetPasswordSuccess: false,
  resetPasswordLoading: false,
  resetPasswordError: null,
  createUserSuccess: false,
  createUserLoading: false,
  createUserError: null,
  resendWelcomeEmailLoading: false,
  resendWelcomeEmailError: null,
  resendWelcomeEmailSuccess: false,
  isCreatingTemplate: false,
  createTemplateSuccess: false,
  createTemplateError: null,
  changeMyPasswordLoading: false,
  changeMyPasswordSuccess: false,
  healthCheckReport: null,
  healthCheckLoading: false,
  healthCheckError: null,
  healthCheckRepairLoading: false,
  ibHealthCheckReport: null,
  ibHealthCheckLoading: false,
  ibHealthCheckError: null,
  ibHealthCheckRepairLoading: false,
  ibHealthCheckRepairJob: null,
  bhBriefingSyncResult: null,
  bhBriefingSyncLoading: false,
  bhBriefingSyncError: null,
  bhBriefingAccounts: {
    orgId: null,
    organization: null,
    accounts: [],
    loading: false,
    error: null,
  },
  dataIntegrityReport: null,
  dataIntegrityLoading: false,
  dataIntegrityError: null,
  updateOrgRlSettingLoading: false,
  updateOrgRlSettingTenantId: null,
  updateOrgWeeklyDigestTenantId: null,
  updateOrgProductTelemetricsTenantId: null,
  updateOrgCoverageAnalysisTenantId: null,
  orgProductTelemetry: {
    metrics: [],
    users: [],
    loading: false,
    error: null,
    orgId: null,
  },
  adminMetadataInfo: {
    loading: false,
    error: null,
    data: null,
  },
  cronSettings: {
    tempPublicBriefingParallelCount: null,
    loading: false,
    saving: false,
    loadError: null,
  },
  portfolioMapsSettings: {
    config: null,
    loading: false,
    saving: false,
    loadError: null,
  },
};

export interface CreateTenantPayload {
  organization_name: string;
  slug: string;
  domain: string;
  admin_name: string;
  admin_email: string;
  phone?: string;
  city?: string;
  country?: string;
  join_date?: string;
  description?: string;
}

export const fetchStatistics = createAsyncThunk(
  "admin/fetchStatistics",
  async (_, { rejectWithValue }) => {
    try {
      const response = await get(API_URLS.fetchAdminStatistics());
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch statistics"
      );
    }
  }
);

export const fetchOrganizations = createAsyncThunk(
  "admin/fetchOrganizations",
  async (_, { rejectWithValue }) => {
    try {
      const response = await get(API_URLS.fetchAdminOrganizations());
      return response.items;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch organizations"
      );
    }
  }
);

export interface UpdateOrganizationRlSettingPayload {
  tenantId: string;
  relationshipAssessments: boolean;
}

export const updateOrganizationRlSettingConfig = createAsyncThunk<
  { tenantId: string; relationshipAssessments: boolean },
  UpdateOrganizationRlSettingPayload,
  { rejectValue: string }
>(
  "admin/updateOrganizationRlSettingConfig",
  async ({ tenantId, relationshipAssessments }, { rejectWithValue }) => {
    try {
      await put(API_URLS.updateOrganizationRlSettingConfig(tenantId), {
        value: {
          relationship_assessments: relationshipAssessments,
        },
      });
      return { tenantId, relationshipAssessments };
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.detail ||
          error.response?.data?.message ||
          "Failed to update RL settings"
      );
    }
  }
);

export interface UpdateOrganizationWeeklyDigestPayload {
  tenantId: string;
  enabled: boolean;
}

export const updateOrganizationWeeklyDigestConfig = createAsyncThunk<
  { tenantId: string; enabled: boolean },
  UpdateOrganizationWeeklyDigestPayload,
  { rejectValue: string }
>(
  "admin/updateOrganizationWeeklyDigestConfig",
  async ({ tenantId, enabled }, { rejectWithValue }) => {
    try {
      await put(API_URLS.updateOrganizationWeeklyDigestConfig(tenantId), {
        value: { enabled },
      });
      return { tenantId, enabled };
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.detail ||
          error.response?.data?.message ||
          "Failed to update Weekly Digest settings"
      );
    }
  }
);

export interface UpdateOrganizationProductTelemetricsPayload {
  tenantId: string;
  enabled: boolean;
}

export const updateOrganizationProductTelemetricsConfig = createAsyncThunk<
  { tenantId: string; enabled: boolean },
  UpdateOrganizationProductTelemetricsPayload,
  { rejectValue: string }
>(
  "admin/updateOrganizationProductTelemetricsConfig",
  async ({ tenantId, enabled }, { rejectWithValue }) => {
    try {
      await put(API_URLS.updateOrganizationProductTelemetricsConfig(tenantId), {
        value: { enabled },
      });
      return { tenantId, enabled };
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.detail ||
          error.response?.data?.message ||
          "Failed to update Product Telemetrics settings"
      );
    }
  }
);

export interface UpdateOrganizationCoverageAnalysisPayload {
  tenantId: string;
  enabled: boolean;
}

export const updateOrganizationCoverageAnalysisConfig = createAsyncThunk<
  { tenantId: string; enabled: boolean },
  UpdateOrganizationCoverageAnalysisPayload,
  { rejectValue: string }
>(
  "admin/updateOrganizationCoverageAnalysisConfig",
  async ({ tenantId, enabled }, { rejectWithValue }) => {
    try {
      await put(API_URLS.updateOrganizationCoverageAnalysisConfig(tenantId), {
        value: { enabled },
      });
      return { tenantId, enabled };
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.detail ||
          error.response?.data?.message ||
          "Failed to update Coverage Analysis settings"
      );
    }
  }
);

export const fetchAdminPortfolioMapsConfig = createAsyncThunk<
  PortfolioMapsConfig,
  void,
  { rejectValue: string }
>("admin/fetchAdminPortfolioMapsConfig", async (_, { rejectWithValue }) => {
  try {
    const response = await get(API_URLS.fetchAdminPortfolioMapsConfig());
    const content = response?.content;
    if (!content || typeof content !== "object") {
      return rejectWithValue("Invalid Portfolio Maps config response");
    }
    return content as PortfolioMapsConfig;
  } catch (error: unknown) {
    return rejectWithValue(
      getOrgTelemetryErrorMessage(
        error,
        "Failed to load Portfolio Maps config."
      )
    );
  }
});

export const updateAdminPortfolioMapsConfig = createAsyncThunk<
  PortfolioMapsConfig,
  PortfolioMapsConfig,
  { rejectValue: string }
>(
  "admin/updateAdminPortfolioMapsConfig",
  async (config, { rejectWithValue }) => {
    try {
      const response = await put(
        API_URLS.updateAdminPortfolioMapsConfig(),
        config
      );
      const content = response?.content;
      if (content && typeof content === "object") {
        return content as PortfolioMapsConfig;
      }
      return config;
    } catch (error: unknown) {
      return rejectWithValue(
        getOrgTelemetryErrorMessage(
          error,
          "Failed to save Portfolio Maps config."
        )
      );
    }
  }
);

export const fetchAllUsers = createAsyncThunk(
  "admin/fetchAllUsers",
  async (_, { rejectWithValue }) => {
    try {
      const response = await get(API_URLS.fetchAdminUsers());
      return response.items;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch users"
      );
    }
  }
);

export const fetchAdminActiveUsers = createAsyncThunk(
  "admin/fetchAdminActiveUsers",
  async (status: string, { rejectWithValue }) => {
    try {
      const response = await get(API_URLS.fetchAdminActiveUsers(status));
      return response.items;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch active users"
      );
    }
  }
);

export const fetchAuthLogs = createAsyncThunk(
  "admin/fetchAuthLogs",
  async (
    params: {
      event_type?: string;
      days?: number;
      page?: number;
      limit?: number;
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await get(API_URLS.fetchAuthLogs(params));
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch auth logs"
      );
    }
  }
);

export const createTenant = createAsyncThunk(
  "admin/createTenant",
  async (payload: CreateTenantPayload, { rejectWithValue }) => {
    try {
      const response = await post(API_URLS.createTenant(), payload);
      return response?.content ?? response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.response?.message ||
          error.response?.data?.message ||
          error.response?.data?.detail ||
          "Failed to create tenant"
      );
    }
  }
);

export const deleteTenant = createAsyncThunk(
  "admin/deleteTenant",
  async (tenantId: string, { rejectWithValue }) => {
    try {
      await del(API_URLS.deleteTenant(tenantId));
      return tenantId;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to delete tenant"
      );
    }
  }
);

export const fetchOrganizationLevels = createAsyncThunk(
  "admin/fetchOrganizationLevels",
  async (_, { rejectWithValue }) => {
    try {
      const response = await get(API_URLS.fetchOrganizationLevels());
      return response.items || response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch organization levels"
      );
    }
  }
);

export interface CreateLevelPayload {
  level_code: string;
  name: string;
  description: string;
  guidance?: string;
}

export interface RelationshipLevelUpdatePayload {
  level_code: string;
  name: string;
  description: string;
  guidance: string;
}

const readApiError = (error: any, fallback: string): string => {
  const raw = error?.message;
  if (typeof raw === "string") {
    try {
      const parsed = JSON.parse(raw);
      const message = parsed?.message ?? parsed?.detail;
      if (typeof message === "string" && message.trim()) return message;
      if (Array.isArray(message) && message[0]?.msg) return message[0].msg;
    } catch {
      if (raw.trim() && !raw.trim().startsWith("{")) return raw;
    }
  }
  return (
    error?.response?.data?.response?.message ||
    error?.response?.data?.message ||
    error?.response?.data?.detail ||
    fallback
  );
};

export const createOrganizationLevel = createAsyncThunk(
  "admin/createOrganizationLevel",
  async (payload: CreateLevelPayload, { rejectWithValue }) => {
    try {
      const response = await post(API_URLS.createOrganizationLevel(), payload);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to create organization level"
      );
    }
  }
);

export const updateOrganizationLevel = createAsyncThunk(
  "admin/updateOrganizationLevel",
  async (
    { id, payload }: { id: string; payload: CreateLevelPayload },
    { rejectWithValue }
  ) => {
    try {
      const response = await put(API_URLS.updateOrganizationLevel(id), payload);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to update organization level"
      );
    }
  }
);

export const deleteOrganizationLevel = createAsyncThunk(
  "admin/deleteOrganizationLevel",
  async (levelId: string, { rejectWithValue }) => {
    try {
      await del(API_URLS.deleteOrganizationLevel(levelId));
      return levelId;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to delete organization level"
      );
    }
  }
);

const extractRelationshipLevels = (response: any): OrganizationLevel[] => {
  const content = response?.content ?? response;
  if (Array.isArray(content?.items)) return content.items;
  if (Array.isArray(content)) return content;
  return [];
};

const extractRelationshipLevel = (response: any): OrganizationLevel => {
  const content = response?.content ?? response;
  return content as OrganizationLevel;
};

export const fetchRelationshipLevels = createAsyncThunk(
  "admin/fetchRelationshipLevels",
  async (_, { rejectWithValue }) => {
    try {
      const response = await get(API_URLS.fetchRelationshipLevels());
      return extractRelationshipLevels(response);
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch relationship levels"
      );
    }
  }
);

export const updateRelationshipLevel = createAsyncThunk(
  "admin/updateRelationshipLevel",
  async (
    { id, payload }: { id: string; payload: RelationshipLevelUpdatePayload },
    { rejectWithValue }
  ) => {
    try {
      const response = await put(API_URLS.updateRelationshipLevel(id), {
        level_code: payload.level_code,
        name: payload.name,
        description: payload.description,
        guidance: payload.guidance,
      });
      return extractRelationshipLevel(response);
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to update relationship level"
      );
    }
  }
);

export interface DeleteUserPayload {
  tenant_schema: string;
  user_id: string;
}

export const deleteAdminUser = createAsyncThunk(
  "admin/deleteAdminUser",
  async (payload: DeleteUserPayload, { rejectWithValue }) => {
    try {
      await post(API_URLS.deleteAdminUser(), payload);
      return payload.user_id;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to delete user"
      );
    }
  }
);

export interface UpdateUserPayload {
  tenant_schema: string;
  user_id: string;
  email: string;
  full_name: string;
  role: string;
  position?: string;
  status: string;
}

export const updateAdminUser = createAsyncThunk(
  "admin/updateAdminUser",
  async (payload: UpdateUserPayload, { rejectWithValue }) => {
    try {
      const response = await put(API_URLS.updateAdminUser(), payload);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to update user"
      );
    }
  }
);

export interface ResetPasswordPayload {
  tenant_schema: string;
  user_id: string;
}

export const resetUserPassword = createAsyncThunk(
  "admin/resetUserPassword",
  async (payload: ResetPasswordPayload, { rejectWithValue }) => {
    try {
      await post(API_URLS.resetUserPassword(), payload);
      return payload.user_id;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to reset password"
      );
    }
  }
);

export interface CreateUserPayload {
  tenant_schema_name: string;
  email: string;
  full_name: string;
  role: string;
  phone?: string;
  position?: string;
  city?: string;
  country?: string;
}

export const createAdminUser = createAsyncThunk(
  "admin/createAdminUser",
  async (payload: CreateUserPayload, { rejectWithValue }) => {
    try {
      const response = await post(API_URLS.createAdminUser(), payload);
      return response?.content ?? response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.response?.message ||
          error.response?.data?.message ||
          error.response?.data?.detail ||
          "Failed to create user"
      );
    }
  }
);

export const resendWelcomeEmail = createAsyncThunk(
  "admin/resendWelcomeEmail",
  async (userId: string, { rejectWithValue }) => {
    try {
      const response = await post(API_URLS.resendWelcomeEmail(userId));
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to send invitation email"
      );
    }
  }
);

export const fetchAssessmentTemplates = createAsyncThunk(
  "admin/fetchAssessmentTemplates",
  async (_, { rejectWithValue }) => {
    try {
      const response = await get(API_URLS.fetchAdminAssessmentTemplates());
      return response.templates || [];
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch assessment templates"
      );
    }
  }
);

export const createAssessmentTemplate = createAsyncThunk(
  "admin/createAssessmentTemplate",
  async (data: CreateTemplateRequest, { rejectWithValue }) => {
    try {
      // Transform payload to backend format
      const response = await post(
        API_URLS.createAdminAssessmentTemplate(),
        data
      );
      return response;
    } catch (error: any) {
      let errorMessage = "Failed to create assessment template";
      if (error?.response?.data) {
        errorMessage =
          error.response.data.detail ||
          error.response.data.message ||
          error.response.data.error?.message ||
          JSON.stringify(error.response.data.errors || error.response.data);
      } else if (error?.message) {
        try {
          const parsedError = JSON.parse(error.message);
          errorMessage =
            parsedError.message ||
            parsedError.detail ||
            parsedError.error?.message ||
            error.message;
        } catch {
          errorMessage = error.message;
        }
      }
      return rejectWithValue(errorMessage);
    }
  }
);

export const updateAssessmentTemplateAdmin = createAsyncThunk(
  "admin/updateAssessmentTemplateAdmin",
  async ({ id, data }: { id: string; data: any }, { rejectWithValue }) => {
    try {
      const response = await put(
        API_URLS.updateAdminAssessmentTemplate(id),
        data
      );
      return response;
    } catch (error: any) {
      let errorMessage = "Failed to update assessment template";
      if (error?.response?.data) {
        errorMessage =
          error.response.data.detail ||
          error.response.data.message ||
          error.response.data.error?.message ||
          JSON.stringify(error.response.data.errors || error.response.data);
      } else if (error?.message) {
        try {
          const parsedError = JSON.parse(error.message);
          errorMessage =
            parsedError.message ||
            parsedError.detail ||
            parsedError.error?.message ||
            error.message;
        } catch {
          errorMessage = error.message;
        }
      }
      return rejectWithValue(errorMessage);
    }
  }
);

export interface ChangeMyPasswordPayload {
  current_password: string;
  new_password: string;
}

export const changeMyPassword = createAsyncThunk(
  "admin/changeMyPassword",
  async (payload: ChangeMyPasswordPayload, { rejectWithValue }) => {
    try {
      const response = await post(API_URLS.changeMyPassword(), payload);
      return response;
    } catch (error: any) {
      const parsedError = JSON.parse(error?.message);

      return rejectWithValue(
        parsedError.message || "Failed to change password. Please try again."
      );
    }
  }
);

export const runHealthCheck = createAsyncThunk(
  "admin/runHealthCheck",
  async (payload: HealthCheckScanPayload, { rejectWithValue }) => {
    try {
      const body = { mode: "scan" as const, org: payload.org };
      const response = await post(API_URLS.healthCheck(), body);
      return response;
    } catch (error: any) {
      const message =
        typeof error?.message === "string" && error.message.startsWith("{")
          ? (() => {
              try {
                const parsed = JSON.parse(error.message);
                return parsed.message || "Failed to run health check.";
              } catch {
                return "Failed to run health check. Please try again.";
              }
            })()
          : error?.message || "Failed to run health check. Please try again.";
      return rejectWithValue(message);
    }
  }
);

export const runHealthCheckRepair = createAsyncThunk(
  "admin/runHealthCheckRepair",
  async (payload: HealthCheckRepairPayload, { rejectWithValue }) => {
    try {
      const body = payload.repairAll
        ? { repairAll: true }
        : { orgId: payload.orgId };
      const response = await post(API_URLS.healthCheckRepair(), body);
      return response;
    } catch (error: any) {
      const message =
        typeof error?.message === "string" && error.message.startsWith("{")
          ? (() => {
              try {
                const parsed = JSON.parse(error.message);
                return parsed.message || "Repair failed.";
              } catch {
                return "Failed to repair. Please try again.";
              }
            })()
          : error?.message || "Failed to repair. Please try again.";
      return rejectWithValue(message);
    }
  }
);

const parseIbHealthCheckError = (error: any, fallback: string): string => {
  const raw = error?.message;
  if (typeof raw === "string" && raw.startsWith("{")) {
    try {
      const parsed = JSON.parse(raw);
      return (
        parsed.response?.message || parsed.message || parsed.detail || fallback
      );
    } catch {
      return raw || fallback;
    }
  }
  return raw || fallback;
};

const IB_HEALTH_SCAN_POLL_MS = 2500;
const IB_HEALTH_SCAN_POLL_MAX_ATTEMPTS = 720; // ~30 minutes at 2.5s
const IB_HEALTH_REPAIR_POLL_MS = 2500;
const IB_HEALTH_REPAIR_POLL_MAX_ATTEMPTS = 1440; // ~60 minutes at 2.5s

const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

const pollIbHealthCheckScanReport = async (): Promise<any> => {
  for (let attempt = 0; attempt < IB_HEALTH_SCAN_POLL_MAX_ATTEMPTS; attempt++) {
    if (attempt > 0) {
      await sleep(IB_HEALTH_SCAN_POLL_MS);
    }
    const statusResponse = await get(API_URLS.ibHealthCheckStatus());
    const job = statusResponse?.content ?? statusResponse;
    const status = String(job?.status || "").toLowerCase();
    if (status === "completed" && job?.report) {
      return job.report;
    }
    if (status === "failed") {
      throw new Error(
        job?.error || "IB Health Check scan could not be completed."
      );
    }
    // idle / running — keep polling after first tick once triggered
  }
  throw new Error(
    "IB Health Check scan is still running. Check backend logs and try again later."
  );
};

const pollIbHealthCheckRepairJob = async (
  onUpdate?: (job: any) => void
): Promise<any> => {
  for (
    let attempt = 0;
    attempt < IB_HEALTH_REPAIR_POLL_MAX_ATTEMPTS;
    attempt++
  ) {
    if (attempt > 0) {
      await sleep(IB_HEALTH_REPAIR_POLL_MS);
    }
    const statusResponse = await get(API_URLS.ibHealthCheckRepairStatus());
    const job = statusResponse?.content ?? statusResponse;
    if (onUpdate) {
      onUpdate(job);
    }
    const status = String(job?.status || "").toLowerCase();
    if (status === "completed" || status === "failed" || status === "stopped") {
      return job;
    }
  }
  throw new Error(
    "IB Health Check repair is still running. Check the activity log and try again later."
  );
};

export const runIbHealthCheck = createAsyncThunk(
  "admin/runIbHealthCheck",
  async (payload: IbHealthCheckScanPayload, { rejectWithValue }) => {
    try {
      try {
        await post(API_URLS.ibHealthCheck(), {
          org: payload.org,
        });
      } catch (startError: any) {
        const startMessage = parseIbHealthCheckError(
          startError,
          "Failed to start IB Health Check."
        );
        // Another tab/request may already own the scan — attach to its result.
        if (!/already running/i.test(startMessage)) {
          return rejectWithValue(startMessage);
        }
      }
      return await pollIbHealthCheckScanReport();
    } catch (error: any) {
      return rejectWithValue(
        parseIbHealthCheckError(error, "Failed to run IB Health Check.")
      );
    }
  }
);

/** One-shot GET of in-memory repair job (live or last cached on this worker). */
export const fetchIbHealthCheckRepairJob = createAsyncThunk(
  "admin/fetchIbHealthCheckRepairJob",
  async (_, { rejectWithValue }) => {
    try {
      const statusResponse = await get(API_URLS.ibHealthCheckRepairStatus());
      return statusResponse?.content ?? statusResponse;
    } catch (error: any) {
      return rejectWithValue(
        parseIbHealthCheckError(
          error,
          "Failed to load IB Health Check repair log."
        )
      );
    }
  }
);

export const runIbHealthCheckRepair = createAsyncThunk(
  "admin/runIbHealthCheckRepair",
  async (
    payload: IbHealthCheckRepairPayload,
    { dispatch, rejectWithValue }
  ) => {
    try {
      const accountIds = (payload.accountIds || [])
        .map(id => String(id || "").trim())
        .filter(Boolean);
      const body = payload.repairAll
        ? {
            repairAll: true,
            generateMissingBriefings:
              payload.generateMissingBriefings !== false,
            forceRegenerateAll: Boolean(payload.forceRegenerateAll),
          }
        : {
            orgId: payload.orgId,
            generateMissingBriefings:
              payload.generateMissingBriefings !== false,
            forceRegenerateAll: Boolean(payload.forceRegenerateAll),
            ...(accountIds.length ? { accountIds } : {}),
          };
      try {
        await post(API_URLS.ibHealthCheckRepair(), body);
      } catch (startError: any) {
        const startMessage = parseIbHealthCheckError(
          startError,
          "Failed to start IB Health Check repair."
        );
        // Another tab/request may already own the repair — attach to its job.
        if (!/already running/i.test(startMessage)) {
          return rejectWithValue(startMessage);
        }
      }
      const job = await pollIbHealthCheckRepairJob(nextJob => {
        dispatch(setIbHealthCheckRepairJob(nextJob));
      });
      if (String(job?.status || "").toLowerCase() === "failed") {
        return rejectWithValue(
          job?.error || "IB Health Check repair could not be completed."
        );
      }
      return job;
    } catch (error: any) {
      return rejectWithValue(
        parseIbHealthCheckError(error, "Failed to repair IB Health Check.")
      );
    }
  }
);

export const stopIbHealthCheckRepair = createAsyncThunk(
  "admin/stopIbHealthCheckRepair",
  async (_, { dispatch, rejectWithValue }) => {
    try {
      await post(API_URLS.ibHealthCheckRepairStop(), {});
      const job = await pollIbHealthCheckRepairJob(nextJob => {
        dispatch(setIbHealthCheckRepairJob(nextJob));
      });
      return job;
    } catch (error: any) {
      return rejectWithValue(
        parseIbHealthCheckError(error, "Failed to stop IB Health Check repair.")
      );
    }
  }
);

export const syncBusinessHealthBriefings = createAsyncThunk(
  "admin/syncBusinessHealthBriefings",
  async (payload: BusinessHealthBriefingSyncPayload, { rejectWithValue }) => {
    try {
      const body: Record<string, unknown> = {};
      if (payload.syncAll) {
        body.syncAll = true;
      } else if (payload.orgId) {
        body.orgId = payload.orgId;
      }
      if (payload.briefingDate) {
        body.briefingDate = payload.briefingDate;
      }
      if (payload.accountIds && payload.accountIds.length > 0) {
        body.accountIds = payload.accountIds;
      }
      const response = await post(API_URLS.businessHealthBriefingsSync(), body);
      return response?.content ?? response;
    } catch (error: any) {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to sync Business Health digests.";
      return rejectWithValue(message);
    }
  }
);

export const fetchBusinessHealthBriefingAccounts = createAsyncThunk(
  "admin/fetchBusinessHealthBriefingAccounts",
  async (orgId: string, { rejectWithValue }) => {
    try {
      const response = await get(
        API_URLS.businessHealthBriefingsAccounts(orgId)
      );
      return response?.content ?? response;
    } catch (error: any) {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to load organization accounts.";
      return rejectWithValue(message);
    }
  }
);

export const fetchDataIntegrityCheck = createAsyncThunk(
  "admin/fetchDataIntegrityCheck",
  async (_, { rejectWithValue }) => {
    try {
      const response = await get(API_URLS.dataIntegrityCheck());
      return response;
    } catch (error: any) {
      const message =
        typeof error?.message === "string" && error.message.startsWith("{")
          ? (() => {
              try {
                const parsed = JSON.parse(error.message);
                return parsed.message || "Failed to run data integrity check.";
              } catch {
                return "Failed to run data integrity check. Please try again.";
              }
            })()
          : error?.message ||
            "Failed to run data integrity check. Please try again.";
      return rejectWithValue(message);
    }
  }
);

const alAssessmentDefaultsError = (error: any, fallback: string): string => {
  if (!error?.message) return fallback;
  try {
    return JSON.parse(error.message)?.message || fallback;
  } catch {
    return error.message;
  }
};

export const fetchAlAssessmentDefaults = createAsyncThunk<
  unknown,
  void,
  { rejectValue: string }
>("admin/fetchAlAssessmentDefaults", async (_, { rejectWithValue }) => {
  try {
    const response = await get(API_URLS.adminAlAssessmentDefaults());
    return response?.content ?? null;
  } catch (error: any) {
    return rejectWithValue(
      alAssessmentDefaultsError(error, "Failed to load AL assessment defaults.")
    );
  }
});
export interface FetchOrgProductTelemetryParams {
  orgId?: string | null;
}

export const fetchOrgProductTelemetry = createAsyncThunk<
  { metrics: OrgMetricRow[]; users: OrgMetricUserRow[]; orgId: string | null },
  FetchOrgProductTelemetryParams | undefined,
  { rejectValue: string }
>("admin/fetchOrgProductTelemetry", async (params, { rejectWithValue }) => {
  try {
    const orgId = params?.orgId ?? null;
    const query: Record<string, string | number> = {
      page: 1,
      page_size: 500,
    };
    if (orgId) query.org_id = orgId;

    const [metricsRes, usersRes] = await Promise.all([
      get(API_URLS.fetchOrgTelemetrics(query)),
      get(API_URLS.fetchOrgTelemetricUsers(query)),
    ]);

    return {
      metrics: unwrapOrgTelemetryList<OrgMetricRow>(metricsRes),
      users: unwrapOrgTelemetryList<OrgMetricUserRow>(usersRes),
      orgId,
    };
  } catch (error: unknown) {
    return rejectWithValue(
      getOrgTelemetryErrorMessage(error, "Failed to load telemetric data")
    );
  }
});

export const fetchAdminMetadataInfo = createAsyncThunk(
  "admin/fetchAdminMetadataInfo",
  async (queryString: string, { rejectWithValue }) => {
    try {
      const base = API_URLS.fetchAdminMetadataInfo();
      const endpoint = queryString ? `${base}?${queryString}` : base;
      return await get(endpoint);
    } catch (error: unknown) {
      return rejectWithValue(
        getOrgTelemetryErrorMessage(error, "Failed to load metadata info")
      );
    }
  }
);

export const fetchAdminCronSettings = createAsyncThunk<
  number,
  void,
  { rejectValue: string }
>("admin/fetchAdminCronSettings", async (_, { rejectWithValue }) => {
  try {
    const response = await get(API_URLS.fetchAdminCronSettings());
    const parsed = Number(
      response?.content?.temp_public_briefing_parallel_count
    );
    if (!Number.isInteger(parsed)) {
      return rejectWithValue("Invalid cron settings response");
    }
    return parsed;
  } catch (error: unknown) {
    return rejectWithValue(
      getOrgTelemetryErrorMessage(error, "Failed to load cron settings.")
    );
  }
});

export interface AlAssessmentTestModeLastRun {
  as_of?: string | null;
  ok?: boolean;
  summary?: string;
  by?: string;
  at?: string;
  dry_run?: boolean;
}

export interface AlAssessmentTestModeOrg {
  tenant_id: string;
  name: string;
  schema_name: string;
  enabled: boolean;
  as_of?: string | null;
  last_run?: AlAssessmentTestModeLastRun | null;
}

export interface AlAssessmentTestModePayload {
  real_today: string;
  orgs: AlAssessmentTestModeOrg[];
}

export const fetchAlAssessmentTestMode = createAsyncThunk<
  AlAssessmentTestModePayload,
  void,
  { rejectValue: string }
>("admin/fetchAlAssessmentTestMode", async (_, { rejectWithValue }) => {
  try {
    const response = await get(API_URLS.adminAlAssessmentTestMode());
    const content = response?.content;
    if (!content) {
      return rejectWithValue("AL assessment test mode is not available.");
    }
    return {
      real_today: String(content.real_today || ""),
      orgs: Array.isArray(content.orgs) ? content.orgs : [],
    };
  } catch (error: any) {
    return rejectWithValue(
      alAssessmentDefaultsError(
        error,
        "Failed to load AL assessment test mode."
      )
    );
  }
});

export const updateAlAssessmentTestMode = createAsyncThunk<
  { real_today: string; org: AlAssessmentTestModeOrg },
  { schema_name: string; enabled: boolean; as_of?: string | null },
  { rejectValue: string }
>("admin/updateAlAssessmentTestMode", async (payload, { rejectWithValue }) => {
  try {
    const response = await put(API_URLS.adminAlAssessmentTestMode(), payload);
    const content = response?.content;
    if (!content?.org) {
      return rejectWithValue("Failed to save AL assessment test mode.");
    }
    return {
      real_today: String(content.real_today || ""),
      org: content.org as AlAssessmentTestModeOrg,
    };
  } catch (error: any) {
    return rejectWithValue(
      alAssessmentDefaultsError(
        error,
        "Failed to save AL assessment test mode."
      )
    );
  }
});

export const runAlAssessmentTestModeJob = createAsyncThunk<
  unknown,
  { schema_name: string; dry_run: boolean },
  { rejectValue: string }
>("admin/runAlAssessmentTestModeJob", async (payload, { rejectWithValue }) => {
  try {
    const response = await post(
      API_URLS.adminAlAssessmentTestModeRun(),
      payload
    );
    return response?.content ?? null;
  } catch (error: any) {
    return rejectWithValue(
      alAssessmentDefaultsError(error, "Failed to run the daily AL job.")
    );
  }
});

export const resetAlAssessmentTestModeData = createAsyncThunk<
  unknown,
  { schema_name: string },
  { rejectValue: string }
>(
  "admin/resetAlAssessmentTestModeData",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await post(
        API_URLS.adminAlAssessmentTestModeReset(),
        payload
      );
      return response?.content ?? null;
    } catch (error: any) {
      return rejectWithValue(
        alAssessmentDefaultsError(error, "Failed to reset AL assessment data.")
      );
    }
  }
);

export const updateAlAssessmentDefaults = createAsyncThunk<
  unknown,
  object,
  { rejectValue: string }
>("admin/updateAlAssessmentDefaults", async (payload, { rejectWithValue }) => {
  try {
    const response = await put(API_URLS.adminAlAssessmentDefaults(), payload);
    return response?.content ?? null;
  } catch (error: any) {
    return rejectWithValue(
      alAssessmentDefaultsError(error, "Failed to save AL assessment defaults.")
    );
  }
});
export const updateAdminCronSettings = createAsyncThunk<
  number,
  number,
  { rejectValue: string }
>(
  "admin/updateAdminCronSettings",
  async (parallelCount, { rejectWithValue }) => {
    try {
      const response = await put(API_URLS.updateAdminCronSettings(), {
        temp_public_briefing_parallel_count: parallelCount,
      });
      const saved = Number(
        response?.content?.temp_public_briefing_parallel_count
      );
      return Number.isInteger(saved) ? saved : parallelCount;
    } catch (error: unknown) {
      return rejectWithValue(
        getOrgTelemetryErrorMessage(error, "Failed to save cron settings.")
      );
    }
  }
);

export const fetchReleaseNoteVersions = createAsyncThunk(
  "admin/fetchReleaseNoteVersions",
  async (search: string | undefined, { rejectWithValue }) => {
    try {
      const response = await get(API_URLS.fetchReleaseNoteVersions(search));
      return (response?.content ?? response) as ReleaseNoteVersionList;
    } catch (error: any) {
      return rejectWithValue(
        readApiError(error, "Failed to load release notes versions")
      );
    }
  }
);

export const sendReleaseNotesEmail = createAsyncThunk(
  "admin/sendReleaseNotesEmail",
  async (
    payload: {
      version_id?: string | null;
      subject: string;
      header_banner: string;
      body_html: string;
      recipients: { org_id: string; user_ids: string[] }[];
    },
    { rejectWithValue }
  ) => {
    try {
      const body = {
        subject: payload.subject,
        header_banner: payload.header_banner,
        body_html: payload.body_html,
        recipients: payload.recipients,
        ...(payload.version_id ? { version_id: payload.version_id } : {}),
      };
      const response = await post(API_URLS.sendReleaseNotesEmail(), body);
      return (response?.content ?? response) as ReleaseNotesSendResult;
    } catch (error: any) {
      return rejectWithValue(
        readApiError(error, "Failed to send the release notes email")
      );
    }
  }
);

export const saveReleaseNoteVersion = createAsyncThunk(
  "admin/saveReleaseNoteVersion",
  async (
    payload: ReleaseNoteVersionWrite & { id?: string },
    { rejectWithValue }
  ) => {
    try {
      const body = {
        version_label: payload.version_label,
        title: payload.title,
        body_html: payload.body_html,
      };
      const response = payload.id
        ? await put(API_URLS.updateReleaseNoteVersion(payload.id), body)
        : await post(API_URLS.createReleaseNoteVersion(), body);
      return (response?.content ?? response) as ReleaseNoteVersion;
    } catch (error: any) {
      return rejectWithValue(
        readApiError(error, "Failed to save the release notes version")
      );
    }
  }
);

export const deleteReleaseNoteVersion = createAsyncThunk(
  "admin/deleteReleaseNoteVersion",
  async (versionId: string, { rejectWithValue }) => {
    try {
      const response = await del(API_URLS.deleteReleaseNoteVersion(versionId));
      return (response?.content ?? response) as ReleaseNoteVersion;
    } catch (error: any) {
      return rejectWithValue(
        readApiError(error, "Failed to delete the release notes version")
      );
    }
  }
);

export const fetchReleaseNotesRecipients = createAsyncThunk(
  "admin/fetchReleaseNotesRecipients",
  async (orgIds: string[], { rejectWithValue }) => {
    try {
      const response = await post(API_URLS.fetchReleaseNotesRecipients(), {
        org_ids: orgIds,
      });
      return (response?.content ?? response) as ReleaseNotesRecipientsPayload;
    } catch (error: any) {
      return rejectWithValue(
        readApiError(error, "Failed to load organization users")
      );
    }
  }
);

export const previewReleaseNotesEmail = createAsyncThunk(
  "admin/previewReleaseNotesEmail",
  async (
    payload: { subject: string; header_banner: string; body_html: string },
    { rejectWithValue }
  ) => {
    try {
      const response = await post(API_URLS.previewReleaseNotesEmail(), {
        subject: payload.subject,
        header_banner: payload.header_banner,
        body_html: payload.body_html,
      });
      return (response?.content ?? response) as ReleaseNotesPreviewPayload;
    } catch (error: any) {
      return rejectWithValue(
        readApiError(error, "Failed to preview the release notes email")
      );
    }
  }
);

const adminSlice = createSlice({
  name: "admin",
  initialState,
  reducers: {
    clearCreateTemplateSuccess: state => {
      state.createTemplateSuccess = false;
      state.createTemplateError = null;
    },
    clearCreateTenantState: state => {
      state.createTenantLoading = false;
      state.createTenantError = null;
      state.createTenantSuccess = false;
    },
    clearDeleteTenantState: state => {
      state.deleteTenantSuccess = false;
      state.deleteTenantLoading = false;
      state.deleteTenantError = null;
      state.deleteTenantNotification = null;
    },
    dismissDeleteTenantNotification: state => {
      state.deleteTenantNotification = null;
    },
    clearCreateLevelState: state => {
      state.createLevelSuccess = false;
      state.createLevelLoading = false;
      state.createLevelError = null;
    },
    clearDeleteLevelState: state => {
      state.deleteLevelSuccess = false;
      state.deleteLevelLoading = false;
      state.deleteLevelError = null;
    },
    clearDeleteUserState: state => {
      state.deleteUserSuccess = false;
      state.deleteUserLoading = false;
      state.deleteUserError = null;
    },
    clearUpdateUserState: state => {
      state.updateUserSuccess = false;
      state.updateUserLoading = false;
      state.updateUserError = null;
    },
    clearResetPasswordState: state => {
      state.resetPasswordSuccess = false;
      state.resetPasswordLoading = false;
      state.resetPasswordError = null;
    },
    clearCreateUserState: state => {
      state.createUserSuccess = false;
      state.createUserLoading = false;
      state.createUserError = null;
    },
    clearResendWelcomeEmailState: state => {
      state.resendWelcomeEmailLoading = false;
      state.resendWelcomeEmailError = null;
      state.resendWelcomeEmailSuccess = false;
    },
    clearChangeMyPasswordState: state => {
      state.changeMyPasswordLoading = false;
      state.changeMyPasswordSuccess = false;
    },
    clearHealthCheckState: state => {
      state.healthCheckReport = null;
      state.healthCheckLoading = false;
      state.healthCheckError = null;
      state.healthCheckRepairLoading = false;
    },
    clearIbHealthCheckState: state => {
      state.ibHealthCheckReport = null;
      state.ibHealthCheckLoading = false;
      state.ibHealthCheckError = null;
      state.ibHealthCheckRepairLoading = false;
      state.ibHealthCheckRepairJob = null;
    },
    setIbHealthCheckRepairJob: (state, action) => {
      state.ibHealthCheckRepairJob = action.payload ?? null;
    },
    clearBhBriefingSyncState: state => {
      state.bhBriefingSyncResult = null;
      state.bhBriefingSyncLoading = false;
      state.bhBriefingSyncError = null;
      state.bhBriefingAccounts = {
        orgId: null,
        organization: null,
        accounts: [],
        loading: false,
        error: null,
      };
    },
    clearDataIntegrityState: state => {
      state.dataIntegrityReport = null;
      state.dataIntegrityLoading = false;
      state.dataIntegrityError = null;
    },
    clearOrgProductTelemetryState: state => {
      state.orgProductTelemetry = {
        metrics: [],
        users: [],
        loading: false,
        error: null,
        orgId: null,
      };
    },
    clearAdminMetadataInfoState: state => {
      state.adminMetadataInfo = {
        loading: false,
        error: null,
        data: null,
      };
    },
  },
  extraReducers: builder => {
    builder
      .addCase(fetchStatistics.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchStatistics.fulfilled, (state, action) => {
        state.loading = false;
        state.statistics = action.payload;
      })
      .addCase(fetchStatistics.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchOrganizations.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchOrganizations.fulfilled, (state, action) => {
        state.loading = false;
        state.organizations = action.payload;
      })
      .addCase(fetchOrganizations.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(updateOrganizationRlSettingConfig.pending, (state, action) => {
        state.updateOrgRlSettingLoading = true;
        state.updateOrgRlSettingTenantId = action.meta.arg.tenantId;
      })
      .addCase(updateOrganizationRlSettingConfig.fulfilled, (state, action) => {
        state.updateOrgRlSettingLoading = false;
        state.updateOrgRlSettingTenantId = null;
        const { tenantId, relationshipAssessments } = action.payload;
        const org = state.organizations.find(o => o.tenant_id === tenantId);
        if (org) {
          org.settings = {
            ...org.settings,
            rl_setting_config: {
              ...org.settings?.rl_setting_config,
              relationship_assessments: relationshipAssessments,
            },
          };
        }
      })
      .addCase(updateOrganizationRlSettingConfig.rejected, state => {
        state.updateOrgRlSettingLoading = false;
        state.updateOrgRlSettingTenantId = null;
        // Error toast owned by AdminOrganizationContainer
      })
      .addCase(
        updateOrganizationWeeklyDigestConfig.pending,
        (state, action) => {
          state.updateOrgWeeklyDigestTenantId = action.meta.arg.tenantId;
        }
      )
      .addCase(
        updateOrganizationWeeklyDigestConfig.fulfilled,
        (state, action) => {
          state.updateOrgWeeklyDigestTenantId = null;
          const { tenantId, enabled } = action.payload;
          const org = state.organizations.find(o => o.tenant_id === tenantId);
          if (org) {
            org.settings = {
              ...org.settings,
              weekly_digest: {
                ...org.settings?.weekly_digest,
                enabled,
              },
            };
          }
        }
      )
      .addCase(updateOrganizationWeeklyDigestConfig.rejected, state => {
        state.updateOrgWeeklyDigestTenantId = null;
        // Error toast owned by AdminOrganizationContainer
      })
      .addCase(
        updateOrganizationProductTelemetricsConfig.pending,
        (state, action) => {
          state.updateOrgProductTelemetricsTenantId = action.meta.arg.tenantId;
        }
      )
      .addCase(
        updateOrganizationProductTelemetricsConfig.fulfilled,
        (state, action) => {
          state.updateOrgProductTelemetricsTenantId = null;
          const { tenantId, enabled } = action.payload;
          const org = state.organizations.find(o => o.tenant_id === tenantId);
          if (org) {
            org.settings = {
              ...org.settings,
              product_telemetrics: {
                ...org.settings?.product_telemetrics,
                enabled,
              },
            };
          }
        }
      )
      .addCase(updateOrganizationProductTelemetricsConfig.rejected, state => {
        state.updateOrgProductTelemetricsTenantId = null;
        // Error toast owned by AdminOrganizationContainer
      })
      .addCase(
        updateOrganizationCoverageAnalysisConfig.pending,
        (state, action) => {
          state.updateOrgCoverageAnalysisTenantId = action.meta.arg.tenantId;
        }
      )
      .addCase(
        updateOrganizationCoverageAnalysisConfig.fulfilled,
        (state, action) => {
          state.updateOrgCoverageAnalysisTenantId = null;
          const { tenantId, enabled } = action.payload;
          const org = state.organizations.find(o => o.tenant_id === tenantId);
          if (org) {
            org.settings = {
              ...org.settings,
              coverage_analysis: {
                ...org.settings?.coverage_analysis,
                enabled,
              },
            };
          }
        }
      )
      .addCase(updateOrganizationCoverageAnalysisConfig.rejected, state => {
        state.updateOrgCoverageAnalysisTenantId = null;
        // Error toast owned by AdminOrganizationContainer
      })
      .addCase(fetchAllUsers.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAllUsers.fulfilled, (state, action) => {
        state.loading = false;
        state.allUsers = action.payload;
      })
      .addCase(fetchAllUsers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchAdminActiveUsers.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAdminActiveUsers.fulfilled, (state, action) => {
        state.loading = false;
        state.activeUsers = action.payload;
      })
      .addCase(fetchAdminActiveUsers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchAuthLogs.pending, state => {
        state.authLogsLoading = true;
        state.authLogsError = null;
      })
      .addCase(fetchAuthLogs.fulfilled, (state, action) => {
        state.authLogsLoading = false;
        state.authLogsData = {
          logs: action.payload.logs || [],
          total: action.payload.total || 0,
          page: action.payload.page || 1,
          limit: action.payload.limit || 20,
          total_pages: action.payload.total_pages || 0,
        };
      })
      .addCase(fetchAuthLogs.rejected, (state, action) => {
        state.authLogsLoading = false;
        state.authLogsError = action.payload as string;
      })
      .addCase(createTenant.pending, state => {
        state.createTenantLoading = true;
        state.createTenantError = null;
        state.createTenantSuccess = false;
      })
      .addCase(createTenant.fulfilled, (state, action) => {
        state.createTenantLoading = false;
        state.createTenantSuccess = true;
        state.createTenantError = null;
      })
      .addCase(createTenant.rejected, (state, action) => {
        state.createTenantLoading = false;
        state.createTenantError = action.payload as string;
        state.createTenantSuccess = false;
      })
      .addCase(deleteTenant.pending, state => {
        state.deleteTenantSuccess = false;
        state.deleteTenantLoading = true;
        state.deleteTenantError = null;
        state.deleteTenantNotification = null;
      })
      .addCase(deleteTenant.fulfilled, (state, action) => {
        state.deleteTenantSuccess = true;
        state.deleteTenantLoading = false;
        state.deleteTenantNotification = {
          type: "success",
          message: "Organization Deleted Successfully",
          details: "The organization has been deleted successfully.",
        };
        state.organizations = state.organizations.filter(
          org => org.tenant_id !== action.payload
        );
      })
      .addCase(deleteTenant.rejected, (state, action) => {
        state.deleteTenantSuccess = false;
        state.deleteTenantLoading = false;
        const err = action.payload as string;
        state.deleteTenantError = err;
        state.deleteTenantNotification = {
          type: "error",
          message: "Failed to Delete Organization",
          details:
            err ||
            "An error occurred while deleting the organization. Please try again.",
        };
      })
      .addCase(fetchOrganizationLevels.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchOrganizationLevels.fulfilled, (state, action) => {
        state.loading = false;
        state.organizationLevels = action.payload;
      })
      .addCase(fetchOrganizationLevels.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(createOrganizationLevel.pending, state => {
        state.createLevelSuccess = false;
        state.createLevelLoading = true;
        state.createLevelError = null;
      })
      .addCase(createOrganizationLevel.fulfilled, (state, action) => {
        state.createLevelSuccess = true;
        state.createLevelLoading = false;
        state.organizationLevels.push(action.payload);
        toast.success("Level Created Successfully");
      })
      .addCase(createOrganizationLevel.rejected, (state, action) => {
        state.createLevelSuccess = false;
        state.createLevelLoading = false;
        state.createLevelError = action.payload as string;
        toast.error(
          (action.payload as string) ||
            "An error occurred while creating the level. Please try again."
        );
      })
      .addCase(updateOrganizationLevel.pending, state => {
        state.createLevelSuccess = false;
        state.createLevelLoading = true;
        state.createLevelError = null;
      })
      .addCase(updateOrganizationLevel.fulfilled, (state, action) => {
        state.createLevelSuccess = true;
        state.createLevelLoading = false;
        const index = state.organizationLevels.findIndex(
          level => level.id === action.payload.id
        );
        if (index !== -1) {
          state.organizationLevels[index] = action.payload;
        }
        toast.success("Level Updated Successfully");
      })
      .addCase(updateOrganizationLevel.rejected, (state, action) => {
        state.createLevelSuccess = false;
        state.createLevelLoading = false;
        state.createLevelError = action.payload as string;
        toast.error(
          (action.payload as string) ||
            "An error occurred while updating the level. Please try again."
        );
      })
      .addCase(deleteOrganizationLevel.pending, state => {
        state.deleteLevelSuccess = false;
        state.deleteLevelLoading = true;
        state.deleteLevelError = null;
      })
      .addCase(deleteOrganizationLevel.fulfilled, (state, action) => {
        state.deleteLevelSuccess = true;
        state.deleteLevelLoading = false;
        state.organizationLevels = state.organizationLevels.filter(
          level => level.id.toString() !== action.payload
        );
        toast.success("Level Deleted Successfully");
      })
      .addCase(deleteOrganizationLevel.rejected, (state, action) => {
        state.deleteLevelSuccess = false;
        state.deleteLevelLoading = false;
        state.deleteLevelError = action.payload as string;
        toast.error(
          (action.payload as string) ||
            "An error occurred while deleting the level. Please try again."
        );
      })
      .addCase(fetchRelationshipLevels.pending, state => {
        state.relationshipLevelsLoading = true;
        state.error = null;
      })
      .addCase(fetchRelationshipLevels.fulfilled, (state, action) => {
        state.relationshipLevelsLoading = false;
        state.relationshipLevels = action.payload;
      })
      .addCase(fetchRelationshipLevels.rejected, (state, action) => {
        state.relationshipLevelsLoading = false;
        state.error = action.payload as string;
      })
      .addCase(updateRelationshipLevel.pending, state => {
        state.updateRelationshipLevelLoading = true;
        state.error = null;
      })
      .addCase(updateRelationshipLevel.fulfilled, (state, action) => {
        state.updateRelationshipLevelLoading = false;
        const index = state.relationshipLevels.findIndex(
          level => String(level.id) === String(action.payload.id)
        );
        if (index !== -1) {
          state.relationshipLevels[index] = action.payload;
        }
        toast.success("Relationship level updated successfully");
      })
      .addCase(updateRelationshipLevel.rejected, (state, action) => {
        state.updateRelationshipLevelLoading = false;
        toast.error(
          (action.payload as string) ||
            "An error occurred while updating the relationship level."
        );
      })
      .addCase(deleteAdminUser.pending, state => {
        state.deleteUserSuccess = false;
        state.deleteUserLoading = true;
        state.deleteUserError = null;
      })
      .addCase(deleteAdminUser.fulfilled, (state, action) => {
        state.deleteUserSuccess = true;
        state.deleteUserLoading = false;
        state.allUsers = state.allUsers.filter(
          user => user.user_id !== action.payload
        );
      })
      .addCase(deleteAdminUser.rejected, (state, action) => {
        state.deleteUserSuccess = false;
        state.deleteUserLoading = false;
        state.deleteUserError = action.payload as string;
      })
      .addCase(updateAdminUser.pending, state => {
        state.updateUserSuccess = false;
        state.updateUserLoading = true;
        state.updateUserError = null;
      })
      .addCase(updateAdminUser.fulfilled, (state, action) => {
        state.updateUserSuccess = true;
        state.updateUserLoading = false;
        const index = state.allUsers.findIndex(
          user => user.user_id === action.payload.user_id
        );
        if (index !== -1) {
          state.allUsers[index] = action.payload;
        }
      })
      .addCase(updateAdminUser.rejected, (state, action) => {
        state.updateUserSuccess = false;
        state.updateUserLoading = false;
        state.updateUserError = action.payload as string;
      })
      .addCase(resetUserPassword.pending, state => {
        state.resetPasswordSuccess = false;
        state.resetPasswordLoading = true;
        state.resetPasswordError = null;
      })
      .addCase(resetUserPassword.fulfilled, state => {
        state.resetPasswordSuccess = true;
        state.resetPasswordLoading = false;
        state.resetPasswordError = null;
      })
      .addCase(resetUserPassword.rejected, (state, action) => {
        state.resetPasswordLoading = false;
        state.resetPasswordSuccess = false;
        state.resetPasswordError = action.payload as string;
      })
      .addCase(createAdminUser.pending, state => {
        state.createUserLoading = true;
        state.createUserSuccess = false;
        state.createUserError = null;
      })
      .addCase(createAdminUser.fulfilled, (state, action) => {
        state.createUserLoading = false;
        state.createUserSuccess = true;
        state.allUsers.push(action.payload);
      })
      .addCase(createAdminUser.rejected, (state, action) => {
        state.createUserLoading = false;
        state.createUserSuccess = false;
        state.createUserError = action.payload as string;
      })
      .addCase(resendWelcomeEmail.pending, state => {
        state.resendWelcomeEmailLoading = true;
        state.resendWelcomeEmailError = null;
        state.resendWelcomeEmailSuccess = false;
      })
      .addCase(resendWelcomeEmail.fulfilled, state => {
        state.resendWelcomeEmailLoading = false;
        state.resendWelcomeEmailSuccess = true;
      })
      .addCase(resendWelcomeEmail.rejected, (state, action) => {
        state.resendWelcomeEmailLoading = false;
        state.resendWelcomeEmailError = action.payload as string;
        state.resendWelcomeEmailSuccess = false;
      })
      .addCase(fetchAssessmentTemplates.pending, state => {
        state.templatesLoading = true;
        state.templatesError = null;
      })
      .addCase(fetchAssessmentTemplates.fulfilled, (state, action) => {
        state.templatesLoading = false;
        state.assessmentTemplates = action.payload;
      })
      .addCase(fetchAssessmentTemplates.rejected, (state, action) => {
        state.templatesLoading = false;
        state.templatesError = action.payload as string;
      })
      .addCase(createAssessmentTemplate.pending, state => {
        state.isCreatingTemplate = true;
        state.createTemplateSuccess = false;
        state.createTemplateError = null;
      })
      .addCase(createAssessmentTemplate.fulfilled, (state, action) => {
        state.isCreatingTemplate = false;
        state.createTemplateSuccess = true;
        state.createTemplateError = null;
        // Optionally add the new template to the list
        if (action.payload) {
          state.assessmentTemplates = [
            action.payload as any,
            ...state.assessmentTemplates,
          ];
        }
      })
      .addCase(createAssessmentTemplate.rejected, (state, action) => {
        state.isCreatingTemplate = false;
        state.createTemplateSuccess = false;
        state.createTemplateError =
          (action.payload as string) || "Failed to create template";
      })
      .addCase(updateAssessmentTemplateAdmin.pending, state => {
        state.isCreatingTemplate = true;
        state.createTemplateError = null;
      })
      .addCase(updateAssessmentTemplateAdmin.fulfilled, state => {
        state.isCreatingTemplate = false;
        state.createTemplateSuccess = true;
        state.createTemplateError = null;
      })
      .addCase(updateAssessmentTemplateAdmin.rejected, (state, action) => {
        state.isCreatingTemplate = false;
        state.createTemplateError =
          (action.payload as string) || "Failed to update template";
      })
      .addCase(changeMyPassword.pending, state => {
        state.changeMyPasswordLoading = true;
        state.changeMyPasswordSuccess = false;
      })
      .addCase(changeMyPassword.fulfilled, state => {
        state.changeMyPasswordLoading = false;
        state.changeMyPasswordSuccess = true;
        toast.success("Password updated successfully");
      })
      .addCase(changeMyPassword.rejected, (state, action) => {
        state.changeMyPasswordLoading = false;
        state.changeMyPasswordSuccess = false;
        toast.error(
          (action.payload as string) ||
            "Failed to update password. Please try again."
        );
      })
      .addCase(runHealthCheck.pending, state => {
        state.healthCheckLoading = true;
        state.healthCheckError = null;
      })
      .addCase(runHealthCheck.fulfilled, (state, action) => {
        state.healthCheckLoading = false;
        state.healthCheckReport = action.payload;
        state.healthCheckError = null;
        const org = action.meta.arg?.org;
        toast.success(
          org
            ? `Health check completed successfully! (${org})`
            : "Health check completed successfully!"
        );
      })
      .addCase(runHealthCheck.rejected, (state, action) => {
        state.healthCheckLoading = false;
        state.healthCheckError = action.payload as string;
        toast.error(
          (action.payload as string) || "Failed to run health check."
        );
      })
      .addCase(runHealthCheckRepair.pending, state => {
        state.healthCheckRepairLoading = true;
      })
      .addCase(runHealthCheckRepair.fulfilled, state => {
        state.healthCheckRepairLoading = false;
        toast.success("Repair completed successfully!");
      })
      .addCase(runHealthCheckRepair.rejected, (state, action) => {
        state.healthCheckRepairLoading = false;
        toast.error(
          (action.payload as string) || "Failed to repair. Please try again."
        );
      })
      .addCase(runIbHealthCheck.pending, state => {
        state.ibHealthCheckLoading = true;
        state.ibHealthCheckError = null;
        toast.info(
          "IB Health Check scan started. Loading all accounts — this can take a few minutes for large orgs."
        );
      })
      .addCase(runIbHealthCheck.fulfilled, (state, action) => {
        state.ibHealthCheckLoading = false;
        state.ibHealthCheckReport = action.payload;
        state.ibHealthCheckError = null;
        toast.success("IB Health Check scan completed.");
      })
      .addCase(runIbHealthCheck.rejected, (state, action) => {
        state.ibHealthCheckLoading = false;
        state.ibHealthCheckError = action.payload as string;
        toast.error(
          (action.payload as string) || "Failed to run IB Health Check."
        );
      })
      .addCase(fetchIbHealthCheckRepairJob.fulfilled, (state, action) => {
        state.ibHealthCheckRepairJob = action.payload ?? null;
      })
      .addCase(runIbHealthCheckRepair.pending, state => {
        state.ibHealthCheckRepairLoading = true;
        state.ibHealthCheckRepairJob = {
          status: "running",
          events: [],
          summary: null,
          error: null,
        };
        toast.info(
          "IB Health Check repair started. Open Repair log to watch progress."
        );
      })
      .addCase(runIbHealthCheckRepair.fulfilled, (state, action) => {
        state.ibHealthCheckRepairLoading = false;
        state.ibHealthCheckRepairJob = action.payload;
        const status = String(action.payload?.status || "").toLowerCase();
        // Stopped toast comes from stopIbHealthCheckRepair when the user cancels.
        if (status !== "stopped") {
          toast.success(
            "IB Health Check repair finished. Run scan again to refresh the report."
          );
        }
      })
      .addCase(runIbHealthCheckRepair.rejected, (state, action) => {
        state.ibHealthCheckRepairLoading = false;
        toast.error(
          (action.payload as string) || "Failed to repair IB Health Check."
        );
      })
      .addCase(stopIbHealthCheckRepair.pending, () => {
        toast.info("Stopping IB Health Check repair…");
      })
      .addCase(stopIbHealthCheckRepair.fulfilled, (state, action) => {
        state.ibHealthCheckRepairLoading = false;
        state.ibHealthCheckRepairJob = action.payload;
        toast.info(
          "IB Health Check repair stopped. Run scan again to see what is still missing."
        );
      })
      .addCase(stopIbHealthCheckRepair.rejected, (state, action) => {
        toast.error(
          (action.payload as string) || "Failed to stop IB Health Check repair."
        );
      })
      .addCase(syncBusinessHealthBriefings.pending, state => {
        state.bhBriefingSyncLoading = true;
        state.bhBriefingSyncError = null;
        toast.info("Syncing complete Business Health digests into ExecBridge…");
      })
      .addCase(syncBusinessHealthBriefings.fulfilled, (state, action) => {
        state.bhBriefingSyncLoading = false;
        state.bhBriefingSyncResult = action.payload;
        state.bhBriefingSyncError = null;
        const stored = Number(action.payload?.stored || 0);
        toast.success(
          stored > 0
            ? `Synced ${stored} complete digest(s) into ExecBridge.`
            : "Sync finished — no new complete digests stored."
        );
      })
      .addCase(syncBusinessHealthBriefings.rejected, (state, action) => {
        state.bhBriefingSyncLoading = false;
        state.bhBriefingSyncError = action.payload as string;
        toast.error(
          (action.payload as string) ||
            "Failed to sync Business Health digests."
        );
      })
      .addCase(fetchBusinessHealthBriefingAccounts.pending, state => {
        state.bhBriefingAccounts.loading = true;
        state.bhBriefingAccounts.error = null;
      })
      .addCase(
        fetchBusinessHealthBriefingAccounts.fulfilled,
        (state, action) => {
          state.bhBriefingAccounts.loading = false;
          state.bhBriefingAccounts.error = null;
          state.bhBriefingAccounts.orgId = action.payload?.org_id || null;
          state.bhBriefingAccounts.organization =
            action.payload?.organization || null;
          state.bhBriefingAccounts.accounts = Array.isArray(
            action.payload?.accounts
          )
            ? action.payload.accounts
            : [];
        }
      )
      .addCase(
        fetchBusinessHealthBriefingAccounts.rejected,
        (state, action) => {
          state.bhBriefingAccounts.loading = false;
          state.bhBriefingAccounts.accounts = [];
          state.bhBriefingAccounts.error = action.payload as string;
        }
      )
      .addCase(fetchDataIntegrityCheck.pending, state => {
        state.dataIntegrityLoading = true;
        state.dataIntegrityError = null;
      })
      .addCase(fetchDataIntegrityCheck.fulfilled, (state, action) => {
        state.dataIntegrityLoading = false;
        state.dataIntegrityReport = action.payload;
        state.dataIntegrityError = null;
      })
      .addCase(fetchDataIntegrityCheck.rejected, (state, action) => {
        state.dataIntegrityLoading = false;
        state.dataIntegrityError = action.payload as string;
        toast.error(
          (action.payload as string) || "Failed to run data integrity check."
        );
      })
      .addCase(fetchOrgProductTelemetry.pending, state => {
        state.orgProductTelemetry.loading = true;
        state.orgProductTelemetry.error = null;
      })
      .addCase(fetchOrgProductTelemetry.fulfilled, (state, action) => {
        state.orgProductTelemetry.loading = false;
        state.orgProductTelemetry.metrics = action.payload.metrics;
        state.orgProductTelemetry.users = action.payload.users;
        state.orgProductTelemetry.orgId = action.payload.orgId;
        state.orgProductTelemetry.error = null;
      })
      .addCase(fetchOrgProductTelemetry.rejected, (state, action) => {
        state.orgProductTelemetry.loading = false;
        state.orgProductTelemetry.metrics = [];
        state.orgProductTelemetry.users = [];
        state.orgProductTelemetry.error =
          (action.payload as string) || "Failed to load telemetric data";
      })
      .addCase(fetchAdminMetadataInfo.pending, state => {
        state.adminMetadataInfo.loading = true;
        state.adminMetadataInfo.error = null;
      })
      .addCase(fetchAdminMetadataInfo.fulfilled, (state, action) => {
        state.adminMetadataInfo.loading = false;
        state.adminMetadataInfo.data = action.payload;
        state.adminMetadataInfo.error = null;
      })
      .addCase(fetchAdminMetadataInfo.rejected, (state, action) => {
        state.adminMetadataInfo.loading = false;
        state.adminMetadataInfo.error =
          (action.payload as string) || "Failed to load metadata info";
      })
      .addCase(fetchAdminCronSettings.pending, state => {
        state.cronSettings.loading = true;
        state.cronSettings.loadError = null;
      })
      .addCase(fetchAdminCronSettings.fulfilled, (state, action) => {
        state.cronSettings.loading = false;
        state.cronSettings.tempPublicBriefingParallelCount = action.payload;
      })
      .addCase(fetchAdminCronSettings.rejected, (state, action) => {
        const message = action.payload || "Failed to load cron settings.";
        state.cronSettings.loading = false;
        state.cronSettings.loadError = message;
        toast.error(message);
      })
      .addCase(updateAdminCronSettings.pending, state => {
        state.cronSettings.saving = true;
      })
      .addCase(updateAdminCronSettings.fulfilled, (state, action) => {
        state.cronSettings.saving = false;
        state.cronSettings.tempPublicBriefingParallelCount = action.payload;
        toast.success("Cron settings saved.");
      })
      .addCase(updateAdminCronSettings.rejected, (state, action) => {
        state.cronSettings.saving = false;
        toast.error(action.payload || "Failed to save cron settings.");
      })
      .addCase(fetchAdminPortfolioMapsConfig.pending, state => {
        state.portfolioMapsSettings.loading = true;
        state.portfolioMapsSettings.loadError = null;
      })
      .addCase(fetchAdminPortfolioMapsConfig.fulfilled, (state, action) => {
        state.portfolioMapsSettings.loading = false;
        state.portfolioMapsSettings.config = action.payload;
      })
      .addCase(fetchAdminPortfolioMapsConfig.rejected, (state, action) => {
        const message =
          action.payload || "Failed to load Portfolio Maps config.";
        state.portfolioMapsSettings.loading = false;
        state.portfolioMapsSettings.loadError = message;
        toast.error(message);
      })
      .addCase(updateAdminPortfolioMapsConfig.pending, state => {
        state.portfolioMapsSettings.saving = true;
      })
      .addCase(updateAdminPortfolioMapsConfig.fulfilled, (state, action) => {
        state.portfolioMapsSettings.saving = false;
        state.portfolioMapsSettings.config = action.payload;
        toast.success("Portfolio Maps settings saved.");
      })
      .addCase(updateAdminPortfolioMapsConfig.rejected, (state, action) => {
        state.portfolioMapsSettings.saving = false;
        toast.error(action.payload || "Failed to save Portfolio Maps config.");
      });
  },
});

export const {
  clearCreateTemplateSuccess,
  clearCreateTenantState,
  clearDeleteTenantState,
  dismissDeleteTenantNotification,
  clearCreateLevelState,
  clearDeleteLevelState,
  clearDeleteUserState,
  clearUpdateUserState,
  clearResetPasswordState,
  clearCreateUserState,
  clearResendWelcomeEmailState,
  clearChangeMyPasswordState,
  clearHealthCheckState,
  clearIbHealthCheckState,
  setIbHealthCheckRepairJob,
  clearBhBriefingSyncState,
  clearDataIntegrityState,
  clearOrgProductTelemetryState,
  clearAdminMetadataInfoState,
} = adminSlice.actions;

export type { AssessmentTemplate };
export default adminSlice.reducer;
